/**
 * Lo que sólo hace el equipo de Deenex, del lado de la pantalla. Una persona de un cliente (Accesos,
 * rol «comercio») ve lo de su comercio, contesta conversaciones y maneja las bajas; armar o lanzar
 * campañas, las plantillas, la Base, la conexión con Twilio, la plata, las líneas y las casillas son
 * del equipo, y el backend se las niega (403 SOLO_EQUIPO). Acá se esconden para que no aparezca un
 * botón que siempre falla.
 *
 * - `<SoloEquipo>`: muestra lo de adentro sólo al equipo (un botón, una tarjeta).
 * - `<PaginaSoloEquipo>`: una página entera del equipo (el asistente, el editor de una plantilla, las
 *   líneas): a un cliente le dice por qué no la ve y lo lleva de vuelta.
 * - `<EsperandoRol>`: mientras se sabe el rol, «Cargando…»; si el canje falló, qué pasó y «Reintentar».
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, Lock, RefreshCw } from "lucide-react";
import { useEsEquipoDeenex, useIntentoRol, useRolActiva } from "../hooks/useEsEquipoDeenex";
import { asegurarRol } from "../rolActiva";

export function SoloEquipo({ children, siNo = null }: { children: ReactNode; siNo?: ReactNode }) {
  return <>{useEsEquipoDeenex() ? children : siNo}</>;
}

export const TEXTO_PAGINA_SOLO_EQUIPO = "Esta parte la maneja el equipo de Deenex. Si necesitás algo de acá, pedíselo a tu contacto.";

/** El aviso para una persona de un cliente que llegó a una página del equipo (por un link o la URL). */
export function AvisoSoloEquipo({ volverA = "/activa/oficial", textoVolver = "Volver" }: { volverA?: string; textoVolver?: string }) {
  return (
    <div role="status" className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
      <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><Lock size={18} /></span>
      <p className="text-sm text-slate-700">{TEXTO_PAGINA_SOLO_EQUIPO}</p>
      <Link to={volverA} className="mt-4 inline-block text-sm font-semibold text-violet-700 hover:underline">{textoVolver}</Link>
    </div>
  );
}

/**
 * Lo que se ve mientras no se sabe el rol (el primer canje está en camino): `cargando` (por defecto, «Cargando…»). Si
 * el canje falló (la red, un 5xx, otra pestaña que cambió de comercio, ningún comercio asignado), qué pasó y
 * «Reintentar», que vuelve a pedirlo una vez (o «Recargar la página», si reintentar no lo arregla). Antes quedaba
 * girando para siempre.
 */
export function EsperandoRol({ cargando }: { cargando?: ReactNode }) {
  const { intentando, error } = useIntentoRol();
  if (!error) {
    return <>{cargando ?? (
      <p className="flex items-center justify-center gap-2 py-12 text-sm text-slate-400">
        <Loader2 size={15} className="animate-spin" /> Cargando…
      </p>
    )}</>;
  }
  return (
    <div role="alert" className="mx-auto max-w-lg rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
      <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><AlertTriangle size={18} /></span>
      <p className="text-sm text-amber-900">{error.texto}</p>
      {error.recargar ? (
        <button type="button" onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <RefreshCw size={14} /> Recargar la página
        </button>
      ) : (
        <button type="button" onClick={() => void asegurarRol()} disabled={intentando}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline disabled:opacity-50">
          {intentando ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Reintentar
        </button>
      )}
    </div>
  );
}

/** Mientras no se sabe el rol (el primer canje está en camino), ni la página ni el aviso: `EsperandoRol`. */
export function PaginaSoloEquipo({ children, volverA, textoVolver }: { children: ReactNode; volverA?: string; textoVolver?: string }) {
  const rol = useRolActiva();
  if (rol === "equipo") return <>{children}</>;
  if (rol === "pendiente") return <EsperandoRol />;
  return <AvisoSoloEquipo volverA={volverA} textoVolver={textoVolver} />;
}

export default SoloEquipo;
